import { useEffect, useMemo, useState } from "react"
import { Link } from "@inertiajs/react"
import { Bot, Inbox, Repeat, Shuffle, Sparkles, User, UserMinus } from "lucide-react"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, MultiSelect } from "@/components/ui/select"
import type { SelectOption } from "@/components/ui/select"
import { CreatableSelect } from "@/components/ui/creatable-select"
import { cn } from "@/lib/cn"
import type {
  AgentPoolOption,
  AssigneeStrategy,
  EscalationPolicy,
  PolicyAgentOption,
  PolicyPriority,
  PolicyReasonOption,
  PolicyTopicOption,
  PolicyUserOption,
  TicketQueueOption,
} from "@/types/escalation_policy"

// Right-side full editor panel for the Auto Triage list+editor layout.
// Holds a local-state buffer of the policy fields. Cancel reverts; Save
// PATCHes via `onSave`. Save button is disabled until the local buffer
// differs from the prop. After a successful Inertia reload the parent
// re-passes the same `policy` (or a refreshed copy) and an effect resyncs
// the buffer.

// Typeahead source for the Appuser-tag chip pickers. Backed by
// /company_tags?q=… which reads from the canonical CompanyTag catalog
// populated on every Appusers::IngestIdentity. Lowercase already; the
// chip picker stores the value verbatim.
async function suggestAppuserTags(q: string): Promise<string[]> {
  const url = `/company_tags?q=${encodeURIComponent(q)}`
  const res = await fetch(url, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  })
  if (!res.ok) return []
  const json: { tags?: string[] } = await res.json()
  return Array.isArray(json.tags) ? json.tags : []
}

const STRATEGY_BUTTONS: { value: AssigneeStrategy; label: string; Icon: typeof User }[] = [
  { value: "unassigned",            label: "Leave unassigned", Icon: UserMinus },
  { value: "specific_user",         label: "Assign user",      Icon: User },
  { value: "random_from_pool",      label: "Random from pool", Icon: Shuffle },
  { value: "round_robin_from_pool", label: "Round robin",      Icon: Repeat },
  { value: "send_to_queue",         label: "Send to queue",    Icon: Inbox },
  { value: "route_to_agent",        label: "Reroute to AI agent", Icon: Bot },
]

const PRIORITY_OPTIONS: SelectOption<string>[] = [
  { value: "",        label: "Leave alone" },
  { value: "urgent",  label: "Urgent" },
  { value: "high",    label: "High" },
  { value: "normal",  label: "Normal" },
  { value: "low",     label: "Low" },
]

type LocalBuffer = {
  name: string
  active: boolean
  match_appuser_tags_include: string[]
  match_appuser_tags_exclude: string[]
  match_reason_keys_include: string[]
  match_reason_keys_exclude: string[]
  match_topic_keys_include: string[]
  match_topic_keys_exclude: string[]
  match_min_recent_escalations: number | null
  match_recent_window_days: number | null
  assignee_strategy: AssigneeStrategy
  assignee_user_id: number | null
  assignee_agent_pool_id: number | null
  assignee_ticket_queue_id: number | null
  prefer_topic_affinity: boolean
  reroute_agent_id: number | null
  priority_override: PolicyPriority | null
  sla_first_response_minutes: number | null
  sla_idle_response_minutes: number | null
  sla_resolution_minutes: number | null
}

function bufferFrom(p: EscalationPolicy): LocalBuffer {
  return {
    name: p.name,
    active: p.active,
    match_appuser_tags_include: [...p.match_appuser_tags_include],
    match_appuser_tags_exclude: [...p.match_appuser_tags_exclude],
    match_reason_keys_include:  [...p.match_reason_keys_include],
    match_reason_keys_exclude:  [...p.match_reason_keys_exclude],
    match_topic_keys_include:   [...p.match_topic_keys_include],
    match_topic_keys_exclude:   [...p.match_topic_keys_exclude],
    match_min_recent_escalations: p.match_min_recent_escalations,
    match_recent_window_days:     p.match_recent_window_days,
    assignee_strategy:    p.assignee_strategy,
    assignee_user_id:     p.assignee_user_id,
    assignee_agent_pool_id: p.assignee_agent_pool_id,
    assignee_ticket_queue_id: p.assignee_ticket_queue_id,
    prefer_topic_affinity: p.prefer_topic_affinity,
    reroute_agent_id:      p.reroute_agent_id,
    priority_override:     p.priority_override,
    sla_first_response_minutes: p.sla_first_response_minutes,
    sla_idle_response_minutes:  p.sla_idle_response_minutes,
    sla_resolution_minutes:     p.sla_resolution_minutes,
  }
}

function bufferEquals(a: LocalBuffer, b: LocalBuffer): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

type Props = {
  policy: EscalationPolicy
  users: PolicyUserOption[]
  agents: PolicyAgentOption[]
  reasonOptions: PolicyReasonOption[]
  topicOptions: PolicyTopicOption[]
  agentPools: AgentPoolOption[]
  ticketQueues: TicketQueueOption[]
  editable: boolean
  ruleNumber: number
  onSave: (id: number, body: Partial<EscalationPolicy>) => void
  onDelete: (id: number) => void
  onToggleActive: (id: number, active: boolean) => void
}

export function PolicyEditorPanel({
  policy,
  users,
  agents,
  reasonOptions,
  topicOptions,
  agentPools,
  ticketQueues,
  editable,
  ruleNumber,
  onSave,
  onDelete,
  onToggleActive,
}: Props) {
  const original = useMemo(() => bufferFrom(policy), [policy])
  const [buf, setBuf] = useState<LocalBuffer>(original)

  // Re-sync local buffer whenever the policy prop changes (Inertia reload).
  useEffect(() => {
    setBuf(bufferFrom(policy))
  }, [policy])

  const dirty = !bufferEquals(buf, original)
  const removed = policy.diffStatus === "removed"
  const disabled = !editable || removed

  const isCatchAll =
    buf.match_appuser_tags_include.length === 0 &&
    buf.match_appuser_tags_exclude.length === 0 &&
    buf.match_reason_keys_include.length === 0 &&
    buf.match_reason_keys_exclude.length === 0 &&
    buf.match_topic_keys_include.length === 0 &&
    buf.match_topic_keys_exclude.length === 0 &&
    !buf.match_min_recent_escalations

  const reasonSelectOptions: SelectOption<string>[] = reasonOptions.map((r) => ({
    value: r.key,
    label: `${r.agentKey} · ${r.name}`,
  }))
  const topicSelectOptions: SelectOption<string>[] = topicOptions.map((t) => ({
    value: t.key,
    label: t.name,
  }))
  const userSelectOptions: SelectOption<number>[] = users.map((u) => ({
    value: u.id,
    label: `${u.name} (${u.email})`,
  }))
  const agentSelectOptions: SelectOption<number>[] = agents.map((a) => ({
    value: a.id,
    label: a.name,
  }))
  const poolSelectOptions: SelectOption<number>[] = agentPools.map((p) => ({
    value: p.id,
    label: `${p.name} (${p.memberCount} member${p.memberCount === 1 ? "" : "s"})`,
  }))
  const queueSelectOptions: SelectOption<number>[] = ticketQueues.map((q) => ({
    value: q.id,
    label: q.name,
  }))

  const update = <K extends keyof LocalBuffer>(key: K, value: LocalBuffer[K]) =>
    setBuf((prev) => ({ ...prev, [key]: value }))

  const cancel = () => setBuf(original)

  const submit = () => {
    onSave(policy.id, {
      name: buf.name,
      active: buf.active,
      match_appuser_tags_include: buf.match_appuser_tags_include,
      match_appuser_tags_exclude: buf.match_appuser_tags_exclude,
      match_reason_keys_include:  buf.match_reason_keys_include,
      match_reason_keys_exclude:  buf.match_reason_keys_exclude,
      match_topic_keys_include:   buf.match_topic_keys_include,
      match_topic_keys_exclude:   buf.match_topic_keys_exclude,
      match_min_recent_escalations: buf.match_min_recent_escalations,
      match_recent_window_days:     buf.match_recent_window_days,
      assignee_strategy:    buf.assignee_strategy,
      assignee_user_id:     buf.assignee_strategy === "specific_user" ? buf.assignee_user_id : null,
      assignee_agent_pool_id:
        buf.assignee_strategy === "random_from_pool" || buf.assignee_strategy === "round_robin_from_pool"
          ? buf.assignee_agent_pool_id : null,
      assignee_ticket_queue_id:
        buf.assignee_strategy === "send_to_queue" ? buf.assignee_ticket_queue_id : null,
      prefer_topic_affinity: buf.prefer_topic_affinity,
      reroute_agent_id:      buf.assignee_strategy === "route_to_agent" ? buf.reroute_agent_id : null,
      priority_override:     buf.priority_override,
      sla_first_response_minutes: buf.sla_first_response_minutes,
      sla_idle_response_minutes:  buf.sla_idle_response_minutes,
      sla_resolution_minutes:     buf.sla_resolution_minutes,
    })
  }

  // Soft warning, not a block — see plan: idle SLA without first-response
  // SLA means the initial wait window is untracked, which is almost always
  // a config mistake but operators may have a reason for it. Yellow note,
  // no save-disable.
  const slaIdleWithoutFirstResponse =
    buf.sla_idle_response_minutes != null && buf.sla_first_response_minutes == null

  // Inline-create handler for both pickers below. Posts to the resource
  // controller, returns the newly-minted option so CreatableSelect can
  // call onChange with it. Failures bubble as console errors — the
  // operator's input stays in the field so they can retry.
  async function createTicketQueue(name: string): Promise<SelectOption<number> | null> {
    const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
    const res = await fetch("/ticket_queues", {
      method:      "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": token ?? "",
        "Content-Type": "application/json",
        "Accept":       "application/json",
      },
      body: JSON.stringify({ ticket_queue: { name } }),
    })
    if (!res.ok) return null
    const data = await res.json() as { id: number; name: string }
    return { value: data.id, label: data.name }
  }

  async function createAgentPool(name: string): Promise<SelectOption<number> | null> {
    const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
    const res = await fetch("/agent_pools", {
      method:      "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": token ?? "",
        "Content-Type": "application/json",
        "Accept":       "application/json",
      },
      body: JSON.stringify({ agent_pool: { name } }),
    })
    if (!res.ok) return null
    const data = await res.json() as { id: number; name: string; memberCount?: number }
    const count = data.memberCount ?? 0
    return { value: data.id, label: `${data.name} (${count} member${count === 1 ? "" : "s"})` }
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <div className="text-sm font-semibold text-app-text">
          Editing rule #{ruleNumber}
          {removed && <span className="ml-2 text-xs italic text-app-muted">(removed)</span>}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-xs text-app-muted">
          <span>Active</span>
          <ToggleSwitch
            checked={buf.active}
            disabled={disabled}
            onChange={(next) => {
              update("active", next)
              onToggleActive(policy.id, next)
            }}
          />
        </label>
      </div>

      {/* Body — scrollable */}
      <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
        {/* Rule name */}
        <div>
          <Label>Rule name</Label>
          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={buf.name}
              disabled={disabled}
              onChange={(e) => update("name", e.target.value)}
              className="w-full max-w-md"
            />
            {isCatchAll && (
              <span className="inline-flex items-center gap-1 rounded-chip border border-vip-gold/30 bg-vip-goldMute px-2 py-0.5 text-[11px] text-vip-goldSoft">
                <Sparkles size={12} />
                Catch-all — matches every escalation
              </span>
            )}
          </div>
        </div>

        {/* Match conditions */}
        <Section
          title="Match conditions"
          help="All specified conditions must match for this rule to fire."
        >
          <div className="text-[11px] text-app-muted">
            All specified conditions must match.
          </div>

          {/* Tags */}
          <PairLabels left="Appuser tags" leftHelp="Match if any tag in this list is present on the appuser. Suggestions come from tags we've seen on appusers in your company." right="Must not include" />
          <div className="grid gap-2 md:grid-cols-2">
            <ChipPicker
              values={buf.match_appuser_tags_include}
              onChange={(v) => update("match_appuser_tags_include", v)}
              placeholder="vip, enterprise…"
              disabled={disabled}
              suggest={suggestAppuserTags}
            />
            <ChipPicker
              values={buf.match_appuser_tags_exclude}
              onChange={(v) => update("match_appuser_tags_exclude", v)}
              placeholder="add chips…"
              disabled={disabled}
              suggest={suggestAppuserTags}
            />
          </div>

          {/* Reasons */}
          <PairLabels left="Escalation reasons" right="Exclude" />
          <div className="grid gap-2 md:grid-cols-2">
            <MultiPicker
              values={buf.match_reason_keys_include}
              options={reasonSelectOptions}
              onChange={(v) => update("match_reason_keys_include", v)}
              disabled={disabled}
            />
            <MultiPicker
              values={buf.match_reason_keys_exclude}
              options={reasonSelectOptions}
              onChange={(v) => update("match_reason_keys_exclude", v)}
              disabled={disabled}
            />
          </div>

          {/* Topics */}
          <PairLabels left="Topics" right="Exclude" />
          <div className="grid gap-2 md:grid-cols-2">
            <MultiPicker
              values={buf.match_topic_keys_include}
              options={topicSelectOptions}
              onChange={(v) => update("match_topic_keys_include", v)}
              disabled={disabled}
            />
            <MultiPicker
              values={buf.match_topic_keys_exclude}
              options={topicSelectOptions}
              onChange={(v) => update("match_topic_keys_exclude", v)}
              disabled={disabled}
            />
          </div>

          {/* Repeat escalations */}
          <div className="space-y-1.5 pt-1">
            <Label help="Fire only when the appuser has escalated repeatedly in the recent window.">
              Repeat escalations
            </Label>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-app-muted">At least</span>
              <Input
                type="number"
                min={0}
                disabled={disabled}
                value={buf.match_min_recent_escalations ?? ""}
                onChange={(e) =>
                  update(
                    "match_min_recent_escalations",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="N"
                className="w-20"
              />
              <span className="text-app-muted">escalations in last</span>
              <Input
                type="number"
                min={1}
                disabled={disabled}
                value={buf.match_recent_window_days ?? ""}
                onChange={(e) =>
                  update(
                    "match_recent_window_days",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="7"
                className="w-20"
              />
              <span className="text-app-muted">days</span>
            </div>
            <div className="text-[11px] italic text-app-subtle">
              Leave both blank to disable.
            </div>
          </div>
        </Section>

        {/* Routing action */}
        <Section title="Routing action" help="Which teammate or agent gets this escalation?">
          <div className="grid grid-cols-5 gap-2">
            {STRATEGY_BUTTONS.map(({ value, label, Icon }) => {
              const isOn = buf.assignee_strategy === value
              return (
                <button
                  key={value}
                  type="button"
                  disabled={disabled}
                  onClick={() => update("assignee_strategy", value)}
                  className={cn(
                    "inline-flex items-center justify-center gap-1.5 rounded-control px-2 py-2 text-[11px] font-medium transition",
                    isOn
                      ? "bg-brand-red text-white shadow-redGlow"
                      : "border border-border bg-app-panel2 text-app-text hover:bg-white/5",
                    disabled && "opacity-50 cursor-not-allowed",
                  )}
                >
                  {isOn && <Icon size={12} />}
                  <span className="truncate">{label}</span>
                </button>
              )
            })}
          </div>

          <div className="pt-2">
            {buf.assignee_strategy === "unassigned" && (
              <div className="text-[11px] text-app-muted">
                The escalation will remain unassigned.
              </div>
            )}

            {buf.assignee_strategy === "specific_user" && (
              <div className="max-w-md">
                <Select<number>
                  value={userSelectOptions.find((o) => o.value === buf.assignee_user_id) ?? null}
                  onChange={(o) => update("assignee_user_id", o ? o.value : null)}
                  options={userSelectOptions}
                  isDisabled={disabled}
                  placeholder="Pick a teammate…"
                />
              </div>
            )}

            {(buf.assignee_strategy === "random_from_pool" ||
              buf.assignee_strategy === "round_robin_from_pool") && (
              <div className="space-y-2">
                <div className="max-w-md">
                  <CreatableSelect<number>
                    value={poolSelectOptions.find((o) => o.value === buf.assignee_agent_pool_id) ?? null}
                    onChange={(o) => update("assignee_agent_pool_id", o ? o.value : null)}
                    options={poolSelectOptions}
                    onCreate={createAgentPool}
                    isDisabled={disabled}
                    placeholder="Pick a pool or type a new name to create one…"
                    formatCreateLabel={(input) => `Create pool "${input}"`}
                  />
                </div>
                <div className="text-[11px]">
                  <Link href="/agent_pools" className="text-brand-red hover:underline">
                    Manage pools →
                  </Link>
                </div>
                <label className="flex cursor-pointer items-start gap-2 text-xs text-app-text">
                  <input
                    type="checkbox"
                    checked={buf.prefer_topic_affinity}
                    disabled={disabled}
                    onChange={(e) => update("prefer_topic_affinity", e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="inline-flex items-center gap-1.5 font-medium">
                      Prefer topic affinity
                      <InfoTooltip side="right">
                        <div className="space-y-2">
                          <div className="font-semibold text-app-text">How specialists are picked</div>
                          <p>
                            When this is on, the rule prefers teammates who have a track record on
                            the convo's topics. If a specialist exists, the ticket goes to one of
                            them. If none, it falls back to the whole pool.
                          </p>
                          <div className="font-semibold text-app-text">How affinity is computed</div>
                          <ul className="list-disc space-y-1 pl-4">
                            <li>We look at the last <strong>30 days</strong> of escalated tickets.</li>
                            <li>For each teammate and each topic, we score the <strong>positive close rate</strong>: thumbs-up customer ratings divided by every ticket they handled on that topic.</li>
                            <li>If a teammate doesn't close their tickets, the unanswered ones still count in the denominator — they get penalized.</li>
                            <li>A teammate needs at least <strong>5 tickets</strong> handled on a topic to be eligible.</li>
                            <li>The <strong>top 50%</strong> on each topic earn the affinity.</li>
                            <li>Recomputed every night automatically.</li>
                            <li>Manual topic preferences set on a teammate's profile always count too.</li>
                          </ul>
                          <div className="font-semibold text-app-text">Overload escape</div>
                          <p>
                            If every specialist is already holding <strong>25 or more</strong> open
                            escalations, the rule falls back to the full pool so the ticket doesn't
                            pile on a saturated person.
                          </p>
                        </div>
                      </InfoTooltip>
                    </span>
                    <span className="block text-app-muted">
                      Specialists handle this topic when available — falls back to the full pool otherwise.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {buf.assignee_strategy === "route_to_agent" && (
              <div className="max-w-md space-y-1.5">
                <Select<number>
                  value={agentSelectOptions.find((o) => o.value === buf.reroute_agent_id) ?? null}
                  onChange={(o) => update("reroute_agent_id", o ? o.value : null)}
                  options={agentSelectOptions}
                  isDisabled={disabled}
                  placeholder="Pick an AI agent…"
                />
                <div className="text-[11px] text-app-muted">
                  Hands the convo off to another AI agent (e.g. a billing or technical specialist) — no human assignee.
                </div>
              </div>
            )}

            {buf.assignee_strategy === "send_to_queue" && (
              <div className="max-w-md space-y-1.5">
                <CreatableSelect<number>
                  value={queueSelectOptions.find((o) => o.value === buf.assignee_ticket_queue_id) ?? null}
                  onChange={(o) => update("assignee_ticket_queue_id", o ? o.value : null)}
                  options={queueSelectOptions}
                  onCreate={createTicketQueue}
                  isDisabled={disabled}
                  placeholder="Pick a queue or type a new name to create one…"
                  formatCreateLabel={(input) => `Create queue "${input}"`}
                />
                <div className="text-[11px] text-app-muted">
                  Drops the ticket into this queue's lane in the Tickets sidebar. Whoever's on rotation for the queue picks it up — no specific assignee.
                </div>
              </div>
            )}
          </div>
        </Section>

        {/* Priority override */}
        <Section title="Priority override" help="Force-set the escalation's priority regardless of the agent's default.">
          <div className="max-w-xs">
            <Select<string>
              value={PRIORITY_OPTIONS.find((o) => o.value === (buf.priority_override ?? "")) ?? null}
              onChange={(o) =>
                update(
                  "priority_override",
                  !o || o.value === "" ? null : (o.value as PolicyPriority),
                )
              }
              options={PRIORITY_OPTIONS}
              isDisabled={disabled}
              isSearchable={false}
            />
          </div>
          <div className="text-[11px] text-app-muted">
            Use this to override the priority that would otherwise be set.
          </div>
        </Section>

        {/* SLA targets */}
        <Section
          title="Human SLA targets"
          help={
            <div className="space-y-2">
              <div>
                <strong>SLA</strong> = Service Level Agreement. Time targets your team
                commits to once a ticket is escalated to a human.
              </div>
              <div>
                Three independent clocks per ticket:
              </div>
              <ul className="space-y-1 pl-3 list-disc">
                <li><strong>First-response</strong> — escalation → first operator reply.</li>
                <li><strong>Idle response</strong> — rolling clock, resets on each customer
                  message; clears when an operator replies.</li>
                <li><strong>Resolution</strong> — escalation → ticket closed.</li>
              </ul>
              <div className="text-app-muted">
                Values are in minutes. Leave blank to inherit the company default.
              </div>
            </div>
          }
        >
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label help="Time to send the first human/AI reply after the convo escalates.">
                First-response (min)
              </Label>
              <Input
                type="number"
                min={1}
                disabled={disabled}
                value={buf.sla_first_response_minutes ?? ""}
                onChange={(e) =>
                  update(
                    "sla_first_response_minutes",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="inherit"
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <Label help="Maximum gap between successive replies once a conversation is in flight.">
                Idle response (min)
              </Label>
              <Input
                type="number"
                min={1}
                disabled={disabled}
                value={buf.sla_idle_response_minutes ?? ""}
                onChange={(e) =>
                  update(
                    "sla_idle_response_minutes",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="inherit"
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <Label help="Time to fully resolve the escalation end-to-end.">
                Resolution (min)
              </Label>
              <Input
                type="number"
                min={1}
                disabled={disabled}
                value={buf.sla_resolution_minutes ?? ""}
                onChange={(e) =>
                  update(
                    "sla_resolution_minutes",
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
                placeholder="inherit"
                className="w-full"
              />
            </div>
          </div>
          <div className="text-[11px] italic text-app-subtle">
            Leave blank to inherit company default.
          </div>
          {slaIdleWithoutFirstResponse && (
            <div className="rounded-control border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-[11px] text-status-warning">
              Idle SLA without a first-response SLA means the initial wait is untracked.
            </div>
          )}
        </Section>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border px-5 py-3">
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (confirm(`Delete "${policy.name}"?`)) onDelete(policy.id)
          }}
          className={cn(
            "text-xs font-medium text-status-error hover:underline",
            disabled && "opacity-40 cursor-not-allowed hover:no-underline",
          )}
        >
          Delete rule
        </button>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={cancel} disabled={!dirty}>
            Cancel
          </Button>
          <Button variant="red" size="sm" onClick={submit} disabled={!dirty || disabled}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  )
}

// -- subcomponents --------------------------------------------------------

function Label({ children, help }: { children: React.ReactNode; help?: string }) {
  return (
    <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide text-app-muted">
      <span>{children}</span>
      {help && <InfoTooltip side="right">{help}</InfoTooltip>}
    </div>
  )
}

function Section({
  title, help, children,
}: { title: string; help?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-app-text">
        <span>{title}</span>
        {help && <InfoTooltip side="right">{help}</InfoTooltip>}
      </div>
      {children}
    </div>
  )
}

function PairLabels({
  left, right, leftHelp, rightHelp,
}: { left: string; right: string; leftHelp?: string; rightHelp?: string }) {
  return (
    <div className="grid gap-2 md:grid-cols-2 pt-1">
      <div className="flex items-center gap-1.5 text-[11px] text-app-muted">
        <span>{left}</span>
        {leftHelp && <InfoTooltip side="right">{leftHelp}</InfoTooltip>}
      </div>
      <div className="flex items-center gap-1.5 text-[11px] text-app-muted">
        <span>{right}</span>
        {rightHelp && <InfoTooltip side="right">{rightHelp}</InfoTooltip>}
      </div>
    </div>
  )
}

// Free-text chip picker. Optional `suggest(q)` returns autocomplete
// candidates; when supplied, the picker shows a dropdown of matches as
// the operator types and adds on click / arrow-Enter. All values are
// stored lowercase to keep the catalog and the policy matcher in sync.
function ChipPicker({
  values, onChange, placeholder, disabled, suggest,
}: {
  values: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  disabled?: boolean
  suggest?: (q: string) => Promise<string[]>
}) {
  const [draft, setDraft] = useState("")
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [highlight, setHighlight] = useState(0)
  const [focused, setFocused] = useState(false)

  // Debounced suggest — 120ms feels instant without hammering the
  // endpoint on every keystroke. Cancel-on-unmount via the ignore flag.
  useEffect(() => {
    if (!suggest) return
    if (!focused) return
    let ignore = false
    const handle = setTimeout(async () => {
      try {
        const result = await suggest(draft)
        if (ignore) return
        setSuggestions(result.filter((s) => !values.includes(s)))
        setHighlight(0)
      } catch {
        if (!ignore) setSuggestions([])
      }
    }, 120)
    return () => { ignore = true; clearTimeout(handle) }
  }, [draft, suggest, focused, values])

  const commit = (raw: string) => {
    const t = raw.trim().toLowerCase()
    if (!t) return
    if (values.includes(t)) { setDraft(""); return }
    onChange([...values, t])
    setDraft("")
  }

  const showDropdown = !!suggest && focused && suggestions.length > 0

  return (
    <div className="relative">
      <div
        className={cn(
          "flex min-h-9 flex-wrap items-center gap-1.5 rounded-control border border-border bg-white/5 px-2 py-1.5",
          disabled && "opacity-50",
        )}
      >
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="inline-flex items-center gap-1 rounded-chip border border-border bg-app-panel2 px-2 py-0.5 text-xs"
          >
            {v}
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(values.filter((x) => x !== v))}
              className="text-app-muted hover:text-status-error"
              aria-label={`Remove ${v}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 120)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              if (showDropdown && suggestions[highlight]) {
                commit(suggestions[highlight])
              } else {
                commit(draft)
              }
              return
            }
            if (e.key === "ArrowDown" && showDropdown) {
              e.preventDefault()
              setHighlight((h) => Math.min(h + 1, suggestions.length - 1))
              return
            }
            if (e.key === "ArrowUp" && showDropdown) {
              e.preventDefault()
              setHighlight((h) => Math.max(h - 1, 0))
              return
            }
            if (e.key === "Escape") {
              setFocused(false)
              return
            }
            if (e.key === "Backspace" && draft === "" && values.length > 0) {
              e.preventDefault()
              onChange(values.slice(0, -1))
            }
          }}
          placeholder={values.length === 0 ? placeholder : ""}
          className="min-w-24 flex-1 bg-transparent text-sm text-app-text outline-none placeholder:text-app-subtle"
        />
      </div>
      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-control border border-border bg-app-panel2 shadow-card">
          {suggestions.map((s, i) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); commit(s) }}
              onMouseEnter={() => setHighlight(i)}
              className={cn(
                "block w-full px-3 py-1.5 text-left text-xs text-app-text",
                i === highlight ? "bg-white/[0.08]" : "hover:bg-white/[0.05]",
              )}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// Single-line picker for typed-key dimensions (escalation reasons,
// conversation topics). Shows selected values as chips + lets the
// operator search/add inline — no separate "+ Add" row.
function MultiPicker({
  values, options, onChange, disabled,
}: {
  values: string[]
  options: SelectOption<string>[]
  onChange: (next: string[]) => void
  disabled?: boolean
}) {
  const selected: SelectOption<string>[] = values
    .map((v) => options.find((o) => o.value === v))
    .filter((o): o is SelectOption<string> => Boolean(o))

  return (
    <MultiSelect<string>
      value={selected}
      options={options}
      onChange={(next) => {
        const arr = (next ?? []) as readonly SelectOption<string>[]
        onChange(arr.map((o) => o.value))
      }}
      isDisabled={disabled}
      placeholder="Pick or search…"
    />
  )
}

function ToggleSwitch({
  checked, disabled, onChange,
}: { checked: boolean; disabled?: boolean; onChange: (next: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition",
        checked ? "bg-brand-red" : "bg-white/10",
        disabled && "opacity-40 cursor-not-allowed",
      )}
    >
      <span
        className={cn(
          "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition",
          checked ? "translate-x-5" : "translate-x-1",
        )}
      />
    </button>
  )
}
